import {
  EnrichmentPriceItem,
  extractPriceAmount,
  LocalizedText,
} from "@app/shared/models/enrichment-detail.model";
import {
  LIFECYCLE_CANONICAL,
  LIFECYCLE_LABEL,
} from "@app/shared/models/enrichment-list.model";
import { priceFigures, PriceFigures } from "./price-amount.util";
import { sortByPriceOrder } from "./price-order.util";

export interface PriceStatusChip {
  readonly label: string;
  readonly type: string;
}

export function priceStatusChip(
  lifecycleStatus: string | null | undefined,
): PriceStatusChip | null {
  const raw = lifecycleStatus?.trim();
  if (!raw) return null;
  const canonical = LIFECYCLE_CANONICAL[raw.toLowerCase()];
  return {
    label: canonical ? LIFECYCLE_LABEL[canonical] : raw,
    type: raw.toLowerCase(),
  };
}

/** The catalog Product code: the flat field first, then the `ProductCode`
 *  external identifier for payloads that only carry the TMF envelope. */
export function productCodeOf(
  price: Pick<EnrichmentPriceItem, "productCode" | "externalIdentifier">,
): string {
  const flat = price.productCode?.trim();
  if (flat) return flat;
  const hit = (price.externalIdentifier ?? []).find(
    (e) => e?.externalIdentifierType === "ProductCode" && e?.id?.trim(),
  );
  return hit?.id?.trim() ?? "";
}

export type TextResolver = (value: LocalizedText | undefined) => string;

export function priceTitle(
  price: Pick<EnrichmentPriceItem, "systemName" | "name">,
  ordinal: number,
  resolve: TextResolver,
): string {
  return (
    resolve(price.systemName).trim() ||
    resolve(price.name).trim() ||
    `Price ${ordinal}`
  );
}

export interface PriceEntry {
  readonly id: string;
  /** 1-based position in `__detail.prices` (catalog order). Feeds only the
   *  "Price N" fallback title and matches the validators' "Price option N". */
  readonly ordinal: number;
  /** 1-based position in editor order: the "i" of "Price i of N". */
  readonly position: number;
  /** Read-only master sequence, for the offset badge. */
  readonly offset: number | null;
  readonly title: string;
  readonly productCode: string;
  readonly status: PriceStatusChip | null;
  readonly figures: PriceFigures;
}

export function buildPriceEntries(
  prices: readonly EnrichmentPriceItem[],
  resolve: TextResolver,
  locale: string,
): readonly PriceEntry[] {
  const keyed = prices
    .map((price, i) => ({ price, ordinal: i + 1 }))
    .filter(({ price }) => !!price.id);
  const ordered = sortByPriceOrder(keyed, ({ price }) => ({
    offset: price.offset ?? null,
    marketingScore: price.marketingScore ?? null,
    popularScore: price.popularScore ?? null,
    priceValue: extractPriceAmount(price.price),
  }));
  return ordered.map(({ price, ordinal }, i) => ({
    id: price.id,
    ordinal,
    position: i + 1,
    offset: price.offset ?? null,
    title: priceTitle(price, ordinal, resolve),
    productCode: productCodeOf(price),
    status: priceStatusChip(price.lifecycleStatus),
    figures: priceFigures(price, resolve, locale),
  }));
}
